import { useCallback, useEffect, useRef } from 'react';
import type { IUseInactivityDetectorResponse, IUseInactivityDetectorProps } from './IUseInactivityDetector';

const events = ['mousemove', 'mousedown', 'keypress', 'scroll', 'touchstart'];

export const useInactivityTimer = (props: IUseInactivityDetectorProps): IUseInactivityDetectorResponse => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { timeout = 60 * 60 * 1000, onTimeout, autoStartDisabled } = props;

  const resetTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onTimeout();
      stopTimer();
    }, timeout);
  };

  const startTimer = useCallback(() => {
    for (const event of events) {
      window.addEventListener(event, handleActivity);
    }
    window.addEventListener('storage', handleStorage);
    resetTimer();
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    for (const event of events) {
      window.removeEventListener(event, handleActivity);
    }
    window.removeEventListener('storage', handleStorage);
  }, []);

  const handleActivity = useCallback(() => {
    resetTimer();
    // Update last active timestamp in localStorage
    localStorage.setItem('lastActive', Date.now().toString());
  }, []);

  const handleStorage = useCallback((e: StorageEvent) => {
    if (e.key === 'lastActive' && e.newValue) {
      resetTimer(); // Reset timer if other tab reports activity
    }
  }, []);

  useEffect(() => {
    if (!autoStartDisabled) {
      startTimer();
    }

    return () => {
      stopTimer();
    };
  }, [timeout, onTimeout, autoStartDisabled]);

  return {
    startTimer,
    stopTimer,
  };
};
